import { ajax } from "jquery";
import { EventEmitter } from "events";
import VisitorService from "../services/VisitorService";
import {
  PopoverInvite,
  InvitationType,
  InviteDetails,
  InlineImageInvite,
  InlineChatInvite,
  InlineInviteContentType,
  PreEngagementMessages
} from "../types";
import StoreContainer from "./StoreContainer";
import ParentWindowEventStore from "./ParentWindowEventStore";

function buildMacroRegex(macro: string) {
  const expression = "\\{(\\s*)" + macro + "(\\s*)\\}";
  return new RegExp(expression, "ig");
}

class InviteStore {
  private _emitter = new EventEmitter();
  private _inviteId: number;
  private _inlineChatInvite: InlineChatInvite;
  private _timedInviteReferences: any = {};
  private _secondsOnPage = 0;
  private _secondsKey = "secondstimer";

  private _manualPingRef: any;
  private readonly _manualPingInterval: number = 1000 * 60;
  private _inviteConversionId: string;
  private _parentWindowEventStore: ParentWindowEventStore;
  private _inviteAcceptMessage: string;

  constructor(parentWindowEventStore: ParentWindowEventStore) {
    this._parentWindowEventStore = parentWindowEventStore;
  }

  onReceivePopoverInvite(callback: (invite: PopoverInvite) => void) {
    this._emitter.addListener("receivePopoverInvite", callback);
  }

  onReceiveInlineImageInvite(callback: (invite: InlineImageInvite) => void) {
    this._emitter.addListener("receiveInlineImageInvite", callback);
  }

  onReceiveInlineChatInvite(callback: (invite: InlineChatInvite) => void) {
    this._emitter.addListener("receiveInlineChatInvite", callback);
  }

  closePopoverInvite() {
    this._emitter.emit("closePopoverInvite");
  }

  onClosePopoverInvite(callback: () => void) {
    this._emitter.addListener("closePopoverInvite", callback);
  }

  closeInlineInvite() {
    this._emitter.emit("closeInlineInvite");
  }

  onCloseInlineInvite(callback: () => void) {
    this._emitter.addListener("closeInlineInvite", callback);
  }

  getInviteConversionId() {
    return this._inviteConversionId;
  }

  async accept() {
    this._inviteConversionId = await VisitorService.acceptInvite(
      StoreContainer.SettingStore.getSiteId(),
      this._inviteId,
      StoreContainer.VisitorStore.getVisitorId(),
      StoreContainer.GroupStore.getActiveOrPageGroupId()
    );
  }

  reject() {
    VisitorService.rejectInvite(
      StoreContainer.SettingStore.getSiteId(),
      this._inviteId,
      StoreContainer.VisitorStore.getVisitorId()
    );
  }

  async handleRecievedTimedInvite(timerExpression: string, inviteId: number) {
    this._timedInviteReferences[inviteId] = setInterval(() => {
      const expression = timerExpression.replace(
        "{Page.Time}",
        this._secondsOnPage.toString()
      );
      if (eval(expression)) {
        this.handleReceivedInvite(inviteId);
        clearInterval(this._timedInviteReferences[inviteId]);
        delete this._timedInviteReferences[inviteId];
      }
    }, 1000);

    if (this._timedInviteReferences[this._secondsKey]) {
      return;
    }

    this._timedInviteReferences[this._secondsKey] = setInterval(() => {
      this._secondsOnPage++;
    }, 1000);
  }

  destroyTimers() {
    Object.keys(this._timedInviteReferences).forEach((key) => {
      clearInterval(this._timedInviteReferences[key]);
    });

    this._timedInviteReferences = {};
    clearTimeout(this._manualPingRef);
  }

  getSecondsOnPage() {
    return this._secondsOnPage;
  }

  setInviteAcceptMessage(inviteAcceptMessage: string) {
    this._inviteAcceptMessage = inviteAcceptMessage;
  }

  getInviteAcceptMessage() {
    return this._inviteAcceptMessage;
  }

  hasInviteAcceptMessage() {
    return !!this._inviteAcceptMessage;
  }

  async handleReceivedInvite(inviteId: number) {
    const data = await VisitorService.getInvite(
      StoreContainer.SettingStore.getSiteId(),
      inviteId,
      StoreContainer.GroupStore.getActiveOrPageGroupId(),
      StoreContainer.VisitorStore.getVisitorId()
    );

    await this.handleInvite(data);
  }

  handleFocus = () => {
    console.log("Focus regained. Restarting manual ping.");
    clearTimeout(this._manualPingRef);
    this.manualInvitePing();
  };

  async startManualInvitePing() {
    this.manualInvitePing();
    this._parentWindowEventStore.addEventListener("focus", this.handleFocus);
  }

  stopManualInvitePing() {
    this._parentWindowEventStore.removeEventListener("focus", this.handleFocus);
  }

  getInviteViews() {
    const inviteViews: Record<number, number> =
      StoreContainer.LocalStorageStore.get("inviteViews") || {};
    return inviteViews;
  }

  wasInviteFired(): boolean {
    const fired = StoreContainer.LocalStorageStore.get("inviteFired");

    if (fired === true || fired === false) {
      return fired;
    }

    return false;
  }

  getPreEngagementMessages(): PreEngagementMessages {
    if (!this._inlineChatInvite) {
      return null;
    }

    const result: PreEngagementMessages = {
      AuthorId: this._inlineChatInvite.authorId,
      AuthorName: this._inlineChatInvite.authorName,
      AuthorType: this._inlineChatInvite.authorType,
      Messages: this._inlineChatInvite.messages
    };

    return result;
  }

  clearInvite() {
    this._emitter.emit("receiveInlineChatInvite", null);
  }

  private async manualInvitePing() {
    const result = await VisitorService.checkManualInvites(
      StoreContainer.SettingStore.getSiteId(),
      StoreContainer.GroupStore.getPageGroupId(),
      StoreContainer.VisitorStore.getVisitorId()
    );
    if (result) {
      await this.handleInvite(result);
    }

    this._manualPingRef = setTimeout(() => {
      this.manualInvitePing();
    }, this._manualPingInterval);
  }

  private handleReceivedInlineImageInvite(data: InviteDetails) {
    const invite: InlineImageInvite = {
      imageUrl: data.invite.Contents,
      expression: data.invite.Expression
    };

    if (data.invite.SecondsToDisplay > 0) {
      invite.secondsToDisplay = data.invite.SecondsToDisplay;
    }

    this._emitter.emit("receiveInlineImageInvite", invite);
    this.persistInviteView(data.invite.InvitationID);
  }

  private handleReceivedInlineChatInvite(data: InviteDetails) {
    if (
      !data.invite.Contents &&
      data.invite.InvitationType === InvitationType.Inline
    ) {
      console.log(
        "inline chat invite received, but not displaying because invite.Contents has no value."
      );

      return;
    }

    let messages = [];

    if (data.invite.InvitationType === InvitationType.Bubble) {
      messages = JSON.parse(data.invite.MessagesBlob);

      if (!messages || !messages.length) {
        console.log(
          "bubble chat invite received, but not displaying because no messages were included."
        );

        return;
      }
    } else {
      messages.push(data.invite.Contents || data.invite.InlineChatMessage);
    }

    const invite: InlineChatInvite = {
      authorName: data.author.Name,
      authorId: data.author.Id,
      authorHeadshotUrl: data.author.HeadshotUrl,
      authorType: data.author.Type,
      messages: messages,
      type: data.invite.InvitationType,
      contentType: data.invite.InlineContentType,
      placeholderText: data.invite.InlinePlaceholderText,
      expression: data.invite.Expression,
      fromBubble: false
    };

    this._inlineChatInvite = invite;
    this._emitter.emit("receiveInlineChatInvite", invite);
    this.persistInviteView(data.invite.InvitationID);
  }

  private async handleReceivedPopoverInvite(data: InviteDetails) {
    const templateJs = await ajax({
      url: `${StoreContainer.SettingStore.getAzureStorageEndpoint()}/invitations/${
        data.invite.UniqueGalleryItemID
      }/invitation-template.js`
    });

    // replace `Velaro.InvitationTemplate = ` at the beginning of the file

    let templateJson = templateJs.replace(
      /Velaro.InvitationTemplate\s*=\s*/g,
      ""
    );

    // replace possible semicolon at the end of the file

    templateJson = templateJson.replace(/;$/g, "");

    let html = JSON.parse(templateJson).Invitation;

    html = html.replace(buildMacroRegex("close"), '<div data-js="close">');
    html = html.replace(buildMacroRegex("endclose"), "</div>");
    html = html.replace(buildMacroRegex("chat"), '<div data-js="chat">');
    html = html.replace(buildMacroRegex("endchat"), "</div>");
    html = html.replace(buildMacroRegex("message"), data.invite.Contents);

    const css = await ajax({
      url: `${StoreContainer.SettingStore.getAzureStorageEndpoint()}/invitations/${
        data.invite.UniqueGalleryItemID
      }/invitation.css`
    });

    const invite: PopoverInvite = {
      html,
      css,
      animationStartPosition: data.invite.StartingAnimationPosition,
      animationStopPosition: data.invite.EndingAnimationPosition,
      expression: data.invite.Expression
    };

    if (data.invite.SecondsToDisplay > 0) {
      invite.secondsToDisplay = data.invite.SecondsToDisplay;
    }

    this._emitter.emit("receivePopoverInvite", invite);
    this.persistInviteView(data.invite.InvitationID);
  }

  private async handleInvite(data: InviteDetails) {
    this._inviteId = data.invite.InvitationID;

    if (
      StoreContainer.FrameStore.isPopOut() ||
      StoreContainer.FrameStore.isExpanded()
    ) {
      return;
    }

    switch (data.invite.InvitationType) {
      case InvitationType.Inline:
        if (data.invite.InlineContentType === InlineInviteContentType.Image) {
          this.handleReceivedInlineImageInvite(data);
        } else {
          this.handleReceivedInlineChatInvite(data);
        }
        break;
      case InvitationType.Popover:
        await this.handleReceivedPopoverInvite(data);
        break;
      case InvitationType.Bubble:
        this.handleReceivedInlineChatInvite(data);
        break;
      default:
        throw new Error(
          `unknown invitation type ${data.invite.InvitationType}`
        );
    }
  }

  private persistInviteView(inviteId: number) {
    const inviteViews = this.getInviteViews();

    if (inviteViews[inviteId]) {
      inviteViews[inviteId] += 1;
    } else {
      inviteViews[inviteId] = 1;
    }

    StoreContainer.LocalStorageStore.set("inviteViews", inviteViews);
    StoreContainer.LocalStorageStore.set("inviteFired", true);
  }
}

export default InviteStore;
